import { useEffect, useRef, useState } from "react";
import type { KalkStundenAppEntry, Order } from "@/lib/projects";
import { getProjectCustomFieldSchemas } from "@/lib/projects";
import {
  buildLabelIndexFromSchemas,
  computeHoursUsageMeta,
  fetchOrderActualHours,
  finalizeHoursUsageEntry,
  resolvePlannedWorkshopMontage,
  type HoursUsageEntry,
} from "@/pages/projekte/hoursUsage";

function hoursProjectSignature(project: Order): string {
  return JSON.stringify({
    id: project.id,
    hero_project_id: project.hero_project_id ?? null,
    auftragsart_app: project.auftragsart_app ?? null,
    has_app_angebot: project.has_app_angebot ?? null,
    kalk_stunden_app: project.kalk_stunden_app ?? null,
    nachweis_stunden_app: project.nachweis_stunden_app ?? null,
    hero_custom_fields: project.hero_custom_fields ?? null,
  });
}

export function useProjectHoursUsage(project: Order) {
  const [entry, setEntry] = useState<HoursUsageEntry | null>(null);
  const [workshopHours, setWorkshopHours] = useState<number | null>(null);
  const [montageHours, setMontageHours] = useState<number | null>(null);
  const [otherHours, setOtherHours] = useState<KalkStundenAppEntry[]>([]);
  const projectRef = useRef(project);
  projectRef.current = project;
  const entryRef = useRef(entry);
  entryRef.current = entry;
  const loadedIdRef = useRef<number | null>(null);
  const signature = hoursProjectSignature(project);

  useEffect(() => {
    let cancelled = false;
    const current = projectRef.current;
    void (async () => {
      try {
        const schemas = await getProjectCustomFieldSchemas();
        const labelIndex = buildLabelIndexFromSchemas(schemas);
        const planned = resolvePlannedWorkshopMontage(current, labelIndex);
        const meta = computeHoursUsageMeta(current, labelIndex);
        if (!meta.eligible) {
          if (!cancelled) {
            setEntry(null);
            setWorkshopHours(null);
            setMontageHours(null);
            setOtherHours([]);
          }
          return;
        }
        const shown = entryRef.current;
        const keepVisible =
          shown != null && !shown.loading && loadedIdRef.current === current.id;
        if (!cancelled) {
          setWorkshopHours(planned.workshop);
          setMontageHours(planned.montage);
          setOtherHours(planned.other);
          if (!keepVisible) setEntry({ ...meta, loading: true, error: false });
        }
        const loaded = await fetchOrderActualHours(current);
        if (!cancelled) {
          loadedIdRef.current = current.id;
          setEntry((prev) => {
            const next = finalizeHoursUsageEntry(
              meta,
              loaded.actualHours,
              loaded.error,
              loaded.nachweisHours,
            );
            if (
              prev &&
              prev.eligible === next.eligible &&
              prev.plannedHours === next.plannedHours &&
              prev.actualHours === next.actualHours &&
              prev.percentRaw === next.percentRaw &&
              prev.nachweisPlannedHours === next.nachweisPlannedHours &&
              prev.nachweisActualHours === next.nachweisActualHours &&
              prev.nachweisPercentRaw === next.nachweisPercentRaw &&
              prev.loading === next.loading &&
              prev.error === next.error
            ) {
              return prev;
            }
            return next;
          });
        }
      } catch {
        if (!cancelled) {
          setEntry(null);
          setWorkshopHours(null);
          setMontageHours(null);
          setOtherHours([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [signature]);

  return { entry, workshopHours, montageHours, otherHours };
}
